// src/pages/student/StudentSupport.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Container, Row, Col, Card, Table, Button, ButtonGroup,
  Form, InputGroup, Modal, FloatingLabel, Badge, Spinner,
  Toast, ToastContainer, Alert, Dropdown
} from "react-bootstrap";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import duration from "dayjs/plugin/duration";
import Swal from "sweetalert2";
import {
  collection, addDoc, updateDoc, doc, onSnapshot,
  serverTimestamp, query, where
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../services/firebase";
import useSchool from "../../hooks/useSchool";
import { getAuth } from "firebase/auth";
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import { useBrand, hexToRgba } from "./_brand";
import "./support.css";

/* ---------------- helpers ---------------- */
const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);
const fmt = (d, f = "DD MMM YYYY, HH:mm") =>
  d && dayjs(toDate(d)).isValid() ? dayjs(toDate(d)).format(f) : "—";
dayjs.extend(relativeTime);
dayjs.extend(duration);

const StatusBadge = ({ status }) => {
  const s = String(status || "open");
  const map = { open: "warning", in_progress: "info", resolved: "success", closed: "secondary" };
  return <Badge bg={map[s] || "secondary"} className="text-capitalize">{s.replace("_", " ")}</Badge>;
};

const PriorityBadge = ({ priority }) => {
  const p = String(priority || "normal").toLowerCase();
  const map = { low: "success", normal: "secondary", high: "danger" };
  return <Badge bg={map[p] || "secondary"} className="text-capitalize">{p}</Badge>;
};

// SLA mapping (from creation)
const SLA_HOURS = { low: 72, normal: 36, high: 12 };
const slaDue = (createdAt, priority = "normal") => {
  const base = toDate(createdAt);
  if (!base) return null;
  const hours = SLA_HOURS[String(priority || "normal").toLowerCase()] ?? 36;
  return dayjs(base).add(hours, "hour").toDate();
};
const SlaPill = ({ createdAt, priority, status }) => {
  const due = slaDue(createdAt, priority);
  if (!due) return null;
  const now = new Date();
  const ms = +due - +now;
  const over = ms < 0 && status !== "resolved" && status !== "closed";
  const text = over ? `Overdue by ${dayjs.duration(-ms).humanize()}` : `Due ${dayjs(due).fromNow()}`;
  return <Badge bg={over ? "danger" : "light"} text={over ? "light" : "dark"} className="border">{text}</Badge>;
};

const FilePill = ({ href, name }) => (
  <a href={href} target="_blank" rel="noreferrer" className="badge bg-light text-dark border me-2 mb-2">
    <i className="bi bi-paperclip me-1" /> {name || "attachment"}
  </a>
);

/* ---------------- theme utils ---------------- */
const ACCENTS = [
  "#0d6efd","#3b82f6","#1d4ed8","#60a5fa",
  "#dc3545","#ef4444","#991b1b","#f87171",
  "#198754","#22c55e","#065f46","#86efac",
  "#ffc107","#facc15","#d97706","#fde047",
  "#6f42c1","#8b5cf6","#7e22ce","#c4b5fd",
  "#20c997","#06b6d4","#164e63","#67e8f9",
  "#fd7e14","#f97316","#9a3412","#fb923c",
  "#6c757d","#94a3b8","#334155","#e2e8f0",
  "#000000","#ffffff",
];
const normalizeHex6 = (hex) => {
  let v = String(hex || "").trim().replace(/^#/, "");
  if (v.length === 3) v = v.split("").map((x) => x + x).join("");
  return /^[0-9a-f]{6}$/i.test(v) ? `#${v.toLowerCase()}` : "#3b82f6";
};

function AccentDropdown({ active, onPick }) {
  const act = normalizeHex6(active);
  return (
    <Dropdown>
      <Dropdown.Menu style={{ padding: 10, maxHeight: 240, overflowY: "auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 24px)", gap: 8, minWidth: 230 }}>
          {ACCENTS.map((c) => {
            const col = normalizeHex6(c);
            return (
              <button
                key={col}
                type="button"
                title={col}
                onClick={() => onPick && onPick(col)}
                style={{
                  width: 20, height: 20, borderRadius: "50%", background: col,
                  border: "none", outline: "none", cursor: "pointer",
                  transition: "box-shadow .2s, transform .15s",
                  boxShadow: act === col
                    ? `0 0 0 2px rgba(0,0,0,.35), 0 0 0 4px ${hexToRgba(col, .4)}`
                    : `0 0 0 1px rgba(0,0,0,.2)`,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.15)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
              />
            );
          })}
          <label
            style={{
              width: 20, height: 20, borderRadius: "50%", cursor: "pointer",
              border: "1px dashed rgba(0,0,0,.25)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, color: "#666",
            }}
            title="Custom color"
          >
            +
            <input type="color" style={{ display: "none" }} onChange={(e) => onPick && onPick(e.target.value)} />
          </label>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}

/* ---------------- Ticket details modal ---------------- */
function TicketModal({
  show, onHide, ticket, comments,
  onComment, posting,
  onCloseTicket, onReopenTicket,
  onEditTicket, canEdit
}) {
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => { setNote(""); setFiles([]); }, [ticket, show]);

  const onChoose = (e) => setFiles(Array.from(e.target.files || []));
  const removeAt = (i) => setFiles((f) => f.filter((_, idx) => idx !== i));

  const [edit, setEdit] = useState(false);
  const [title, setTitle] = useState(ticket?.title || "");
  const [desc, setDesc] = useState(ticket?.description || "");

  useEffect(() => {
    setTitle(ticket?.title || "");
    setDesc(ticket?.description || "");
    setEdit(false);
  }, [ticket, show]);

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Ticket – {ticket?.title || "—"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!ticket ? null : (
          <Row className="g-3">
            <Col lg={8}>
              <div className="mb-2 small text-muted">#{ticket?.id}</div>
              <div className="mb-2"><b>Status:</b> <StatusBadge status={ticket?.status} /></div>
              <div className="mb-2"><b>Priority:</b> <PriorityBadge priority={ticket?.priority} /></div>
              <div className="mb-2"><b>Category:</b> {ticket?.category || "general"}</div>
              <div className="mb-2"><b>Created:</b> {fmt(ticket?.createdAt)} &nbsp; <SlaPill createdAt={ticket?.createdAt} priority={ticket?.priority} status={ticket?.status} /></div>

              {/* attachments */}
              {Array.isArray(ticket?.attachments) && ticket.attachments.length > 0 && (
                <div className="mb-2">
                  <b>Attachments:</b>
                  <div className="mt-2 d-flex flex-wrap">
                    {ticket.attachments.map((a, idx) => <FilePill key={idx} href={a.url} name={a.name} />)}
                  </div>
                </div>
              )}

              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <b>Description</b>
                  {canEdit && ticket.status === "open" && (
                    <Button size="sm" variant={edit ? "outline-secondary" : "outline-primary"} onClick={() => setEdit((v) => !v)}>
                      {edit ? "Cancel" : "Edit"}
                    </Button>
                  )}
                </div>

                {!edit ? (
                  <div className="mt-1">{ticket?.description || "—"}</div>
                ) : (
                  <>
                    <FloatingLabel label="Title *" className="mb-2">
                      <Form.Control value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" />
                    </FloatingLabel>
                    <FloatingLabel label="Description">
                      <Form.Control as="textarea" style={{ height: 140 }} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Describe…" />
                    </FloatingLabel>
                    <div className="mt-2 text-end">
                      <Button
                        size="sm"
                        onClick={() => onEditTicket({ title, description: desc }, () => setEdit(false))}
                        disabled={posting || !title.trim()}
                      >
                        Save changes
                      </Button>
                    </div>
                  </>
                )}
              </div>

              <div className="mb-2"><b>Add a comment</b></div>
              <FloatingLabel label="Write a comment or update…">
                <Form.Control
                  as="textarea"
                  style={{ height: 100 }}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Write a comment or update…"
                />
              </FloatingLabel>

              <div className="mt-2">
                <Form.Label className="small">Attach files (optional)</Form.Label>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Form.Control ref={fileRef} type="file" multiple onChange={onChoose} />
                  {files.length > 0 && (
                    <div className="d-flex flex-wrap">
                      {files.map((f, i) => (
                        <span key={i} className="badge bg-light text-dark border me-2 mb-2">
                          <i className="bi bi-file-earmark me-1" />
                          {f.name}
                          <Button size="sm" variant="link" className="text-danger ms-1 p-0" onClick={() => removeAt(i)}>
                            <i className="bi bi-x" />
                          </Button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Col>

            <Col lg={4}>
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white fw-semibold">Comments</Card.Header>
                <Card.Body style={{ maxHeight: 320, overflow: "auto" }}>
                  {comments.length ? comments.map(c => (
                    <div key={c.id} className="mb-3">
                      <div className="small">
                        <b>{c.by?.name || "User"}</b>{" "}
                        <span className="text-muted">({c.by?.role || "user"})</span>
                      </div>
                      <div className="small text-muted">{fmt(c.createdAt)}</div>
                      <div className="mt-1">{c.text}</div>
                      {Array.isArray(c.attachments) && c.attachments.length > 0 && (
                        <div className="mt-2 d-flex flex-wrap">
                          {c.attachments.map((a, i) => <FilePill key={i} href={a.url} name={a.name} />)}
                        </div>
                      )}
                    </div>
                  )) : <div className="text-muted small">No comments yet</div>}
                </Card.Body>
              </Card>

              <div className="d-grid gap-2 mt-3">
                <Button
                  className="btn-neo"
                  onClick={() => onComment(note, files, () => { setNote(""); setFiles([]); })}
                  disabled={!note.trim() || posting}
                >
                  {posting ? "Posting…" : "Comment"}
                </Button>

                {ticket.status !== "resolved" && ticket.status !== "closed" ? (
                  <Button className="btn-ghost" onClick={onCloseTicket} disabled={posting}>
                    Mark as Resolved
                  </Button>
                ) : (
                  <Button className="btn-ghost" onClick={onReopenTicket} disabled={posting}>
                    Reopen Ticket
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        )}
      </Modal.Body>
    </Modal>
  );
}

/* ---------------- main page ---------------- */
export default function StudentSupport() {
  const brand = useBrand();
  const { school, loading: loadingSchool, me } = useSchool();

  const particlesInit = useCallback(async (engine) => { await loadSlim(engine); }, []);

  // theme
  const { primary, accent, setPrimary, setAccent } = brand || {};
  const primaryColor = useMemo(() => normalizeHex6(primary || "#3b82f6"), [primary]);
  const accentComputed = useMemo(() => normalizeHex6(accent || primaryColor), [accent, primaryColor]);
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--brand-primary", primaryColor);
    root.style.setProperty("--brand-accent", accentComputed);
  }, [primaryColor, accentComputed]);
  const pickTheme = (c) => { const col = normalizeHex6(c); setPrimary?.(col); setAccent?.(col); };

  // Always prefer Auth for writes so rules (request.auth.uid) match exactly
  const auth = getAuth();
  const authUser = auth.currentUser;

  const myUid =
    authUser?.uid ||
    me?.auth?.uid ||
    me?.uid ||
    me?.id ||
    null;

  const myName =
    authUser?.displayName ||
    me?.displayName ||
    me?.name ||
    authUser?.email ||
    me?.email ||
    "Student";

  const myRole = String(me?.role || "student").toLowerCase();

  // Determine if user is allowed to create (mirrors your rules intent)
  const isInactive = (
    (typeof me?.status === "string" && me.status.toLowerCase() !== "active") ||
    me?.active === false
  );
  const wrongSchool = !!(me?.schoolId && school?.id && me.schoolId !== school.id);
  const canCreate = !!(school?.id && myUid && !isInactive && !wrongSchool);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [qText, setQText] = useState(localStorage.getItem("ss_q") || "");
  const [status, setStatus] = useState(localStorage.getItem("ss_status") || "all");
  const [sortKey, setSortKey] = useState(localStorage.getItem("ss_sort") || "createdAt_desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // create form
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("general");
  const [priority, setPriority] = useState("normal");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [posting, setPosting] = useState(false);

  // modal
  const [show, setShow] = useState(false);
  const [current, setCurrent] = useState(null);
  const [comments, setComments] = useState([]);

  // toasts
  const [toast, setToast] = useState({ show: false, bg: "dark", title: "", body: "" });
  const showToast = (body, { title = "Notice", bg = "dark", delay = 2200 } = {}) => {
    setToast({ show: true, bg, title, body, delay });
  };

  const searchRef = useRef(null);
  const fileRef = useRef(null);

  // ---- tickets: only this student's tickets in this school ----
  useEffect(() => {
    if (!school?.id || !myUid) return;
    setLoading(true);
    const colRef = collection(db, "schools", school.id, "tickets");
    const qy = query(colRef, where("createdBy.uid", "==", myUid)); // strictly my tickets
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const list = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (+toDate(b.createdAt) || 0) - (+toDate(a.createdAt) || 0));
        setRows(list);
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        Swal.fire({ icon: "error", title: "Could not load tickets", text: err?.message || "Try again" });
      }
    );
    return () => unsub();
  }, [school?.id, myUid]);

  // ---- comments stream for selected ticket ----
  useEffect(() => {
    if (!school?.id || !current?.id) { setComments([]); return; }
    const colRef = collection(db, "schools", school.id, "tickets", current.id, "comments");
    const qy = query(colRef, where("type", "==", "public"));
    const unsub = onSnapshot(qy, (snap) => {
      const list = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (+toDate(a.createdAt) || 0) - (+toDate(b.createdAt) || 0));
      setComments(list);
    }, (err) => {
      Swal.fire({ icon: "error", title: "Could not load comments", text: err?.message || "Try again" });
    });
    return () => unsub();
  }, [school?.id, current?.id]);

  // persist filters
  useEffect(() => { localStorage.setItem("ss_q", qText); }, [qText]);
  useEffect(() => { localStorage.setItem("ss_status", status); }, [status]);
  useEffect(() => { localStorage.setItem("ss_sort", sortKey); }, [sortKey]);

  // search + filter + sort
  const filtered = useMemo(() => {
    const q = qText.trim().toLowerCase();
    let list = rows.filter(t => {
      if (status !== "all" && String(t.status || "open") !== status) return false;
      if (!q) return true;
      const hay = [t.title, t.description, t.category, t.priority].join(" ").toLowerCase();
      return hay.includes(q);
    });
    // sort
    const [k, dir] = (sortKey || "createdAt_desc").split("_");
    list = list.sort((a, b) => {
      let av = a[k]; let bv = b[k];
      if (k.includes("At")) { av = +toDate(av) || 0; bv = +toDate(bv) || 0; }
      if (typeof av === "string") av = av.toLowerCase();
      if (typeof bv === "string") bv = bv.toLowerCase();
      if (av < bv) return dir === "asc" ? -1 : 1;
      if (av > bv) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [rows, qText, status, sortKey]);

  // pagination
  const total = filtered.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => { if (page > pages) setPage(1); }, [pages, page]);
  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  // file selection
  const onChoose = (e) => setFiles(Array.from(e.target.files || []));
  const removeAt = (i) => setFiles((f) => f.filter((_, idx) => idx !== i));
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer?.files || []);
    if (dropped.length) setFiles((prev) => prev.concat(dropped));
  };

  const uploadMany = async (ticketId, filesArr) => {
    if (!filesArr?.length) return [];
    const out = [];
    for (const f of filesArr) {
      const safe = f.name.replace(/[^\w.\-]+/g, "_");
      const path = `schools/${school.id}/tickets/${ticketId}/attachments/${Date.now()}_${safe}`;
      const r = ref(storage, path);
      await uploadBytes(r, f);
      const url = await getDownloadURL(r);
      out.push({ name: f.name, path, url, size: f.size || null, type: f.type || null });
    }
    return out;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!school?.id || !myUid) return;

    if (!canCreate) {
      Swal.fire({
        icon: "info",
        title: "You can’t create a ticket right now",
        html: wrongSchool
          ? "Your user record is linked to a different school. Please contact the school admin."
          : "Your account appears inactive. Please contact the school admin to activate it.",
      });
      return;
    }

    if (!title.trim()) {
      return Swal.fire({ icon: "warning", title: "Title is required" });
    }

    setPosting(true);
    try {
      // create ticket first (to get ID)
      const docRef = await addDoc(collection(db, "schools", school.id, "tickets"), {
        title: title.trim(),
        description: description.trim(),
        category: String(category || "general"),
        priority: String(priority || "normal"),
        status: "open",
        attachments: [],
        createdBy: { uid: myUid, name: myName, role: myRole },
        assignee: null,
        schoolId: school.id,
        lastCommentAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // upload attachments (if any)
      let uploaded = [];
      if (files.length) {
        uploaded = await uploadMany(docRef.id, files);
        await updateDoc(doc(db, "schools", school.id, "tickets", docRef.id), {
          attachments: uploaded,
          updatedAt: serverTimestamp(),
        });
      }

      setTitle(""); setDescription(""); setCategory("general"); setPriority("normal"); setFiles([]);
      fileRef.current && (fileRef.current.value = "");
      showToast("Your ticket was created.", { title: "Success", bg: "success" });
    } catch (err) {
      if (err?.code === "permission-denied") {
        Swal.fire({
          icon: "error",
          title: "Not allowed to create ticket",
          html: "Please ask your school admin to ensure your <code>/users/{uid}</code> record exists, is <b>active</b>, and has <b>schoolId</b> set to this school.",
        });
      } else {
        Swal.fire({ icon: "error", title: "Could not create ticket", text: err?.message || "Try again" });
      }
    } finally { setPosting(false); }
  };

  // add comment with optional files
  const addComment = async (text, fileList, after) => {
    if (!current || !text.trim()) return;
    setPosting(true);
    try {
      let uploaded = [];
      if (fileList?.length) uploaded = await uploadMany(current.id, fileList);

      await addDoc(collection(db, "schools", school.id, "tickets", current.id, "comments"), {
        text: text.trim(),
        by: { uid: myUid, name: myName, role: myRole },
        type: "public",
        attachments: uploaded,
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, "schools", school.id, "tickets", current.id), {
        lastCommentAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      showToast("Comment posted.", { title: "Success", bg: "success" });
      after?.();
    } catch (err) {
      if (err?.code === "permission-denied") {
        Swal.fire({
          icon: "error",
          title: "Not allowed to comment",
          html: "Only ticket owners and staff in this school can comment.",
        });
      } else {
        Swal.fire({ icon: "error", title: "Could not add comment", text: err?.message || "Try again" });
      }
    } finally { setPosting(false); }
  };

  const closeTicket = async () => {
    if (!current) return;
    setPosting(true);
    try {
      await updateDoc(doc(db, "schools", school.id, "tickets", current.id), {
        status: "resolved",
        updatedAt: serverTimestamp(),
      });
      showToast("Ticket marked as resolved.", { title: "Done", bg: "success" });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Could not update ticket", text: err?.message || "Try again" });
    } finally { setPosting(false); }
  };

  const reopenTicket = async () => {
    if (!current) return;
    setPosting(true);
    try {
      await updateDoc(doc(db, "schools", school.id, "tickets", current.id), {
        status: "open",
        updatedAt: serverTimestamp(),
      });
      showToast("Ticket reopened.", { title: "Done", bg: "success" });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Could not update ticket", text: err?.message || "Try again" });
    } finally { setPosting(false); }
  };

  const editTicket = async (patch, after) => {
    if (!current) return;
    if (!patch?.title?.trim()) {
      Swal.fire({ icon: "warning", title: "Title is required" });
      return;
    }
    setPosting(true);
    try {
      await updateDoc(doc(db, "schools", school.id, "tickets", current.id), {
        title: patch.title.trim(),
        description: (patch.description || "").trim(),
        updatedAt: serverTimestamp(),
      });
      showToast("Ticket updated.", { title: "Saved", bg: "success" });
      after?.();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Could not save ticket", text: err?.message || "Try again" });
    } finally { setPosting(false); }
  };

  // export visible to CSV
  const exportCSV = () => {
    const header = ["Title", "Category", "Priority", "Status", "Created", "Ticket ID"];
    const lines = filtered.map(t => [
      `"${(t.title || "").replace(/"/g, '""')}"`,
      t.category || "",
      t.priority || "",
      t.status || "",
      fmt(t.createdAt),
      t.id
    ].join(","));
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `my_tickets_${dayjs().format("YYYYMMDD_HHmm")}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  if (loadingSchool) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="py-5 text-center text-muted">
          <Spinner animation="border" size="sm" /> Loading…
        </Card.Body>
      </Card>
    );
  }
  if (!school?.id) return <div className="text-muted">No school selected.</div>;

  const canEdit = !!myUid;

  // sheen ripple (for fancy buttons)
  const sheen = (e) => {
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--rx", `${e.clientX - r.left}px`);
    el.style.setProperty("--ry", `${e.clientY - r.top}px`);
    el.classList.add("clicked");
    setTimeout(() => el.classList.remove("clicked"), 500);
  };

  return (
    <div className="position-relative overflow-hidden">
      {/* FULL-PAGE background */}
      <div
        className="position-fixed top-0 start-0 w-100 h-100"
        style={{
          zIndex: 0,
          background: `
            radial-gradient(1100px 600px at 8% 12%, ${hexToRgba(primaryColor, .10)} 0%, transparent 60%),
            radial-gradient(1000px 560px at 92% 88%, ${hexToRgba(accentComputed,  .10)} 0%, transparent 60%)
          `,
        }}
      />
      <Particles
        id="support-bg-particles"
        className="position-fixed top-0 start-0 w-100 h-100"
        init={particlesInit}
        style={{ zIndex: 1, pointerEvents: "none" }}
        options={{
          fpsLimit: 60,
          fullScreen: { enable: false },
          background: { color: { value: "transparent" } },
          detectRetina: true,
          interactivity: {
            events: { onHover: { enable: true, mode: "repulse" }, onClick: { enable: true, mode: "push" }, resize: true },
            modes: { repulse: { distance: 120, duration: 0.35 }, push: { quantity: 2 } },
          },
          particles: {
            number: { value: 60, density: { enable: true, area: 900 } },
            color: { value: [primaryColor, accentComputed] },
            links: { enable: true, color: hexToRgba(primaryColor, 0.45), distance: 140, opacity: 0.45, width: 1 },
            move: { enable: true, speed: 1.0, outModes: { default: "out" } },
            opacity: { value: 0.55 },
            size: { value: { min: 1, max: 3 } },
          },
          responsive: [
            { maxWidth: 768, options: { particles: { number: { value: 36 } } } },
            { maxWidth: 420, options: { particles: { number: { value: 20 } } } },
          ],
        }}
      />

      {/* Foreground */}
      <Container fluid className="p-0 position-relative" style={{ zIndex: 2 }}>
        {/* Header */}
        <Row className="mb-3 g-2 align-items-center">
          <Col>
            <h5 className="mb-0">Support</h5>
            <small className="text-muted">Raise a ticket and track its status</small>
          </Col>
          <Col className="d-flex justify-content-end align-items-center gap-2">
            <ButtonGroup className="me-2 segmented">
              <Button
                className="seg-btn"
                variant={status === "all" ? "primary" : "outline-primary"}
                onMouseDown={sheen}
                onClick={() => setStatus("all")}
              >
                All
              </Button>
              <Button
                className="seg-btn"
                variant={status === "open" ? "primary" : "outline-primary"}
                onMouseDown={sheen}
                onClick={() => setStatus("open")}
              >
                Open
              </Button>
              <Button
                className="seg-btn"
                variant={status === "in_progress" ? "primary" : "outline-primary"}
                onMouseDown={sheen}
                onClick={() => setStatus("in_progress")}
              >
                In-Progress
              </Button>
              <Button
                className="seg-btn"
                variant={status === "resolved" ? "primary" : "outline-primary"}
                onMouseDown={sheen}
                onClick={() => setStatus("resolved")}
              >
                Resolved
              </Button>
            </ButtonGroup>

            <Dropdown onSelect={(k) => setSortKey(k)}>
              <Dropdown.Toggle variant="outline-secondary" size="sm">
                <i className="bi bi-sort-down" /> Sort
              </Dropdown.Toggle>
              <Dropdown.Menu align="end">
                <Dropdown.Item eventKey="createdAt_desc">Newest first</Dropdown.Item>
                <Dropdown.Item eventKey="createdAt_asc">Oldest first</Dropdown.Item>
                <Dropdown.Item eventKey="priority_desc">Priority ↓</Dropdown.Item>
                <Dropdown.Item eventKey="priority_asc">Priority ↑</Dropdown.Item>
                <Dropdown.Item eventKey="status_asc">Status A→Z</Dropdown.Item>
                <Dropdown.Item eventKey="status_desc">Status Z→A</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <AccentDropdown active={primaryColor} onPick={pickTheme} />
          </Col>
        </Row>

        {/* Creation gating notice */}
        {!canCreate && (
          <Alert variant="warning" className="mb-3">
            {wrongSchool
              ? "You are linked to a different school. Please contact the school admin."
              : "Your account appears inactive. Please contact the school admin to activate it."}
          </Alert>
        )}

        {/* Raise a ticket */}
        <Card className="border-0 shadow-sm mb-3">
          <Card.Header className="bg-white fw-semibold">Raise a Ticket</Card.Header>
          <Card.Body>
            <Form onSubmit={submit} onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={onDrop}>
              <Row className="g-3">
                <Col md={8}>
                  <FloatingLabel label="Title *">
                    <Form.Control
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Brief summary"
                      disabled={!canCreate}
                    />
                  </FloatingLabel>
                </Col>
                <Col md={2}>
                  <FloatingLabel label="Category">
                    <Form.Select value={category} onChange={(e) => setCategory(e.target.value)} disabled={!canCreate}>
                      <option value="general">General</option>
                      <option value="login">Login</option>
                      <option value="fees">Fees</option>
                      <option value="exam">Exam</option>
                      <option value="assignment">Assignment</option>
                      <option value="class">Class</option>
                      <option value="technical">Technical</option>
                    </Form.Select>
                  </FloatingLabel>
                </Col>
                <Col md={2}>
                  <FloatingLabel label="Priority">
                    <Form.Select value={priority} onChange={(e) => setPriority(e.target.value)} disabled={!canCreate}>
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                    </Form.Select>
                  </FloatingLabel>
                </Col>
                <Col md={12}>
                  <FloatingLabel label="Description">
                    <Form.Control
                      as="textarea"
                      style={{ height: 120 }}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Describe your issue or question"
                      disabled={!canCreate}
                    />
                  </FloatingLabel>
                </Col>

                {/* attachments */}
                <Col md={12}>
                  <Form.Label>Attachments (optional)</Form.Label>
                  <div
                    className={`p-3 border rounded ${dragOver ? "bg-light" : ""}`}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                  >
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <Form.Control ref={fileRef} type="file" multiple onChange={onChoose} disabled={!canCreate} />
                      {files.length > 0 && (
                        <div className="d-flex flex-wrap">
                          {files.map((f, i) => (
                            <span key={i} className="badge bg-light text-dark border me-2 mb-2">
                              <i className="bi bi-paperclip me-1" />
                              {f.name}
                              <Button size="sm" variant="link" className="text-danger ms-1 p-0" onClick={() => removeAt(i)}>
                                <i className="bi bi-x" />
                              </Button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
              <div className="mt-3 d-flex justify-content-between align-items-center">
                <div className="small text-muted">
                  SLA: <Badge bg="light" text="dark" className="border">{SLA_HOURS[String(priority).toLowerCase()]}h</Badge> for current priority
                </div>
                <Button
                  className="btn-neo cta-btn"
                  type="submit"
                  disabled={posting || !title.trim() || !canCreate}
                  onMouseDown={sheen}
                >
                  <span className="btn-inner">
                    {posting && <span className="btn-spinner" aria-hidden="true" />}
                    {posting ? "Submitting…" : "Submit Ticket"}
                  </span>
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>

        {/* List of my tickets */}
        <Card className="p-3 border-0 shadow-sm">
          <Row className="g-2 align-items-end mb-2">
            <Col md={7}>
              <Form.Label className="mb-1">Search</Form.Label>
              <InputGroup>
                <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                <Form.Control
                  ref={searchRef}
                  placeholder="Title, description, category…"
                  value={qText}
                  onChange={(e) => setQText(e.target.value)}
                />
                {qText && <Button className="btn-ghost" onClick={() => setQText("")}>Clear</Button>}
              </InputGroup>
            </Col>
            <Col md={2}>
              <Form.Label className="mb-1">Page size</Form.Label>
              <Form.Select value={pageSize} onChange={(e) => setPageSize(+e.target.value)}>
                {[5,10,20,50].map(n => <option key={n} value={n}>{n}</option>)}
              </Form.Select>
            </Col>
            <Col md={3} className="text-end">
              <div className="mb-1">
                <Badge bg="light" text="dark" className="border me-2">
                  {filtered.length} of {rows.length} shown
                </Badge>
                <Button size="sm" className="btn-ghost" onClick={exportCSV}>
                  <i className="bi bi-download me-1" /> Export CSV
                </Button>
              </div>
              <div>
                <Button
                  size="sm"
                  className="btn-ghost me-1"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  ◀
                </Button>
                <span className="small">Page {page} / {pages}</span>
                <Button
                  size="sm"
                  className="btn-ghost ms-1"
                  disabled={page >= pages}
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                >
                  ▶
                </Button>
              </div>
            </Col>
          </Row>

          <div className="table-responsive">
            <Table hover className="align-middle support-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th className="d-none d-lg-table-cell">Created</th>
                  <th className="d-none d-xl-table-cell">SLA</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-5">
                      <Spinner animation="border" size="sm" /> Loading…
                    </td>
                  </tr>
                ) : pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">No tickets yet</td>
                  </tr>
                ) : pageRows.map(t => (
                  <tr key={t.id}>
                    <td className="fw-semibold">
                      {t.title || "—"}
                      {Array.isArray(t.attachments) && t.attachments.length > 0 && (
                        <span className="ms-2 small text-muted" title="Has attachments">
                          <i className="bi bi-paperclip" />
                        </span>
                      )}
                    </td>
                    <td>{t.category || "general"}</td>
                    <td className="text-capitalize"><PriorityBadge priority={t.priority} /></td>
                    <td><StatusBadge status={t.status} /></td>
                    <td className="d-none d-lg-table-cell">{fmt(t.createdAt)}</td>
                    <td className="d-none d-xl-table-cell"><SlaPill createdAt={t.createdAt} priority={t.priority} status={t.status} /></td>
                    <td className="text-end">
                      <Button
                        size="sm"
                        className="btn-ghost"
                        title="Open"
                        onClick={() => { setCurrent(t); setShow(true); }}
                      >
                        <i className="bi bi-box-arrow-up-right" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card>

        {/* Details modal */}
        <TicketModal
          show={show}
          onHide={() => setShow(false)}
          ticket={current}
          comments={comments}
          posting={posting}
          onComment={(text, modalFiles, after) => addComment(text, modalFiles, after)}
          onCloseTicket={closeTicket}
          onReopenTicket={reopenTicket}
          onEditTicket={editTicket}
          canEdit={canEdit}
        />

        {/* Toasts */}
        <ToastContainer position="bottom-end" className="p-3">
          <Toast
            bg={toast.bg}
            show={toast.show}
            delay={toast.delay ?? 2200}
            autohide
            onClose={() => setToast(t => ({ ...t, show: false }))}
          >
            {toast.title ? (
              <Toast.Header closeButton={true}>
                <strong className="me-auto">{toast.title}</strong>
                <small className="text-muted">just now</small>
              </Toast.Header>
            ) : null}
            <Toast.Body className={toast.bg === "warning" ? "text-dark" : "text-white"}>
              {toast.body}
            </Toast.Body>
          </Toast>
        </ToastContainer>
      </Container>

      {/* Styles */}
      <style>{`
        :root{
          --brand-primary: ${primaryColor};
          --brand-accent: ${accentComputed};
        }

        /* fancy segmented buttons */
        .segmented .seg-btn{
          position: relative;
          overflow: hidden;
          border-color: ${hexToRgba(primaryColor,.45)} !important;
          color: var(--bs-body-color);
          transition: background .12s ease, color .12s ease, transform .12s ease, box-shadow .12s ease, border-color .12s ease;
        }
        .segmented .seg-btn:hover{
          background: ${hexToRgba(primaryColor,.10)} !important;
          box-shadow: 0 6px 16px ${hexToRgba(primaryColor,.18)};
          transform: translateY(-1px);
        }
        .segmented .seg-btn.clicked::after{
          content:"";
          position:absolute; left: var(--rx); top: var(--ry);
          width: 0; height: 0; transform: translate(-50%,-50%);
          background: radial-gradient(circle at center, ${hexToRgba(primaryColor,.45)} 0%, transparent 60%);
          animation: ripple .5s ease-out forwards;
          pointer-events:none;
        }
        @keyframes ripple{ from{ width:0; height:0; opacity:.7 } to{ width:220px; height:220px; opacity:0 } }

        /* primary CTA */
        .btn-neo{
          --_bg1: ${hexToRgba(primaryColor, .95)};
          --_bg2: ${hexToRgba(accentComputed, .95)};
          color:#fff !important;
          background: linear-gradient(135deg, var(--_bg1) 0%, var(--_bg2) 100%);
          border: 1px solid ${hexToRgba(primaryColor,.45)};
          box-shadow: 0 10px 18px ${hexToRgba(primaryColor,.24)};
          transition: transform .12s ease, box-shadow .12s ease, filter .12s ease;
          position: relative;
          overflow: hidden;
        }
        .btn-neo .btn-inner{ position: relative; z-index: 1; }
        .btn-neo .btn-spinner{
          width:14px; height:14px; border-radius:50%;
          border:2px solid #fff; border-top-color: transparent; display:inline-block; margin-right:8px;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        .btn-neo:hover{ transform: translateY(-1px); filter: brightness(1.04); box-shadow: 0 14px 26px ${hexToRgba(primaryColor,.30)}; }
        .btn-neo:active{ transform: translateY(0); filter: brightness(.98); box-shadow: 0 6px 14px ${hexToRgba(primaryColor,.24)} inset; }
        .btn-neo.disabled, .btn-neo:disabled { opacity: .65 !important; filter: grayscale(.2); box-shadow: none; }

        /* ghost secondary */
        .btn-ghost{
          color: ${primaryColor} !important;
          background: transparent;
          border: 1px solid ${hexToRgba(primaryColor,.45)};
          transition: transform .12s ease, background .12s ease, box-shadow .12s ease, color .12s ease;
        }
        .btn-ghost:hover{
          background: ${hexToRgba(primaryColor,.10)};
          box-shadow: 0 6px 16px ${hexToRgba(primaryColor,.18)};
          transform: translateY(-1px);
        }

        /* table polish */
        .support-table tbody tr:hover{
          background: ${hexToRgba(primaryColor,.06)};
        }
        .support-table tbody td{
          border-color: ${hexToRgba(primaryColor,.08)};
        }

        /* toast tweak */
        .toast{
          border-radius: 12px;
          border: 1px solid ${hexToRgba(accentComputed,.35)};
          box-shadow: 0 8px 22px ${hexToRgba(primaryColor,.18)};
        }
      `}</style>
    </div>
  );
}
